<template>
  <div class="legal-page">
    <!-- Decorative gradient hero -->
    <header class="legal-hero">
      <div class="legal-hero-bg" aria-hidden="true">
        <span class="lg-sym lg-sym-1">∑</span>
        <span class="lg-sym lg-sym-2">π</span>
        <span class="lg-sym lg-sym-3">∫</span>
        <span class="lg-sym lg-sym-4">√</span>
        <span class="lg-sym lg-sym-5">≠</span>
      </div>

      <div class="legal-hero-inner">
        <div class="legal-hero-icon">
          <slot name="icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </slot>
        </div>
        <h1 class="legal-hero-title">{{ title }}</h1>
        <p v-if="subtitle" class="legal-hero-sub">{{ subtitle }}</p>
        <span class="legal-hero-badge">آخر تحديث: {{ lastUpdated }}</span>
      </div>
    </header>

    <!-- Content card -->
    <main class="legal-body-wrap">
      <article class="legal-card">
        <div class="legal-content">
          <slot />
        </div>

        <!-- Cross link between the two legal pages -->
        <div class="legal-crosslink">
          <router-link v-if="counterpart === 'terms'" to="/terms-of-service" class="legal-crosslink-btn">
            الاطلاع على شروط الاستخدام
            <i class="pi pi-arrow-left"></i>
          </router-link>
          <router-link v-else to="/privacy-policy" class="legal-crosslink-btn">
            الاطلاع على سياسة الخصوصية
            <i class="pi pi-arrow-left"></i>
          </router-link>
        </div>
      </article>
    </main>
  </div>
</template>

<script>
export default {
  name: 'LegalDocument',
  props: {
    title: { type: String, required: true },
    subtitle: { type: String, default: '' },
    lastUpdated: { type: String, required: true },
    // which page to link to at the bottom: 'terms' | 'privacy'
    counterpart: { type: String, default: 'privacy' },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800;900&display=swap');

.legal-page {
  font-family: 'Cairo', sans-serif;
  direction: rtl;
  background: #F9F9F9;
  min-height: calc(100vh - 70px);
  padding-bottom: 3rem;
}

/* ── Hero ── */
.legal-hero {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #02090f 0%, #051629 40%, #F7B424 75%, #FBCE72 100%);
  padding: 3.25rem 1.5rem 2.5rem;
  text-align: center;
}
.legal-hero-bg { position: absolute; inset: 0; pointer-events: none; }
.lg-sym {
  position: absolute;
  font-size: 3rem;
  font-weight: 800;
  color: rgba(255, 255, 255, 0.12);
  user-select: none;
}
.lg-sym-1 { top: 12%; right: 8%; font-size: 3.4rem; }
.lg-sym-2 { top: 55%; right: 18%; font-size: 2.4rem; }
.lg-sym-3 { bottom: 12%; left: 10%; font-size: 3rem; }
.lg-sym-4 { top: 20%; left: 14%; font-size: 2.2rem; }
.lg-sym-5 { bottom: 22%; right: 40%; font-size: 2rem; }

.legal-hero-inner { position: relative; z-index: 1; max-width: 820px; margin: 0 auto; }

.legal-hero-icon {
  width: 72px; height: 72px;
  margin: 0 auto 1.1rem;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.28);
  backdrop-filter: blur(10px);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.18);
}
.legal-hero-icon :deep(svg) { width: 34px; height: 34px; color: #fff; }

.legal-hero-title {
  font-size: clamp(1.7rem, 4vw, 2.5rem);
  font-weight: 900;
  color: #fff;
  line-height: 1.25;
  margin-bottom: 0.5rem;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.18);
}
.legal-hero-sub {
  font-size: clamp(0.9rem, 2vw, 1.05rem);
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.7;
  margin-bottom: 1.1rem;
  max-width: 640px;
  margin-inline: auto;
}
.legal-hero-badge {
  display: inline-block;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.28);
  color: #fff;
  padding: 0.4rem 1rem;
  border-radius: 99px;
  font-size: 0.82rem;
  font-weight: 700;
}

/* ── Body ── */
.legal-body-wrap {
  max-width: 860px;
  margin: -1.75rem auto 0;
  padding: 0 1.25rem;
  position: relative;
  z-index: 2;
}
.legal-card {
  background: #fff;
  border-radius: 24px;
  padding: 2.75rem 2.5rem;
  box-shadow: 0 24px 70px rgba(5, 22, 41, 0.1), 0 4px 16px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.8);
}

/* ── Rich content typography (slot) ── */
.legal-content { color: #374151; font-size: 1rem; line-height: 2; }

.legal-content :deep(h2) {
  font-size: 1.35rem;
  font-weight: 800;
  color: #1F2937;
  margin: 2.25rem 0 0.9rem;
  padding-right: 0.9rem;
  border-right: 4px solid #051629;
  scroll-margin-top: 90px;
}
.legal-content :deep(h2:first-child) { margin-top: 0; }
.legal-content :deep(h3) {
  font-size: 1.08rem;
  font-weight: 800;
  color: #051629;
  margin: 1.4rem 0 0.5rem;
}
.legal-content :deep(p) { margin: 0 0 1rem; color: #374151; }
.legal-content :deep(strong) { color: #1F2937; font-weight: 800; }
.legal-content :deep(a) { color: #051629; font-weight: 700; text-decoration: none; }
.legal-content :deep(a:hover) { color: #F7B424; text-decoration: underline; }

.legal-content :deep(ul) { margin: 0 0 1.1rem; padding-right: 1.4rem; list-style: none; }
.legal-content :deep(ul li) {
  position: relative;
  margin-bottom: 0.6rem;
  padding-right: 1.3rem;
}
.legal-content :deep(ul li::before) {
  content: '';
  position: absolute;
  right: 0; top: 0.75rem;
  width: 8px; height: 8px;
  border-radius: 50%;
  background: linear-gradient(135deg, #051629, #F7B424);
}

.legal-content :deep(.legal-intro) {
  background: #F9F9F9;
  border: 1px solid #E5E7EB;
  border-radius: 16px;
  padding: 1.1rem 1.25rem;
  margin-bottom: 1.75rem;
  font-size: 0.98rem;
}
.legal-content :deep(.legal-note) {
  background: rgba(5, 22, 41, 0.06);
  border: 1px solid rgba(5, 22, 41, 0.18);
  border-radius: 14px;
  padding: 1rem 1.15rem;
  margin: 1.25rem 0;
  font-size: 0.95rem;
}
.legal-content :deep(.legal-contact) {
  background: linear-gradient(135deg, rgba(5, 22, 41, 0.08), rgba(247, 180, 36, 0.06));
  border: 1px solid rgba(5, 22, 41, 0.2);
  border-radius: 18px;
  padding: 1.4rem 1.5rem;
  margin-top: 1rem;
}

/* ── Cross link ── */
.legal-crosslink {
  margin-top: 2.5rem;
  padding-top: 1.75rem;
  border-top: 1px solid #EEF0F5;
  text-align: center;
}
.legal-crosslink-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  background: linear-gradient(135deg, #051629, #F7B424);
  color: #fff;
  text-decoration: none;
  padding: 0.75rem 1.5rem;
  border-radius: 14px;
  font-weight: 800;
  font-size: 0.95rem;
  box-shadow: 0 12px 30px rgba(5, 22, 41, 0.3);
  transition: transform 0.25s, box-shadow 0.25s;
}
.legal-crosslink-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 18px 40px rgba(5, 22, 41, 0.42);
  color: #fff;
}
.legal-crosslink-btn i { font-size: 0.85rem; }

/* ── Responsive ── */
@media (max-width: 600px) {
  .legal-card { padding: 1.9rem 1.25rem; border-radius: 20px; }
  .legal-content { font-size: 0.95rem; line-height: 1.9; }
  .legal-content :deep(h2) { font-size: 1.18rem; }
  .legal-hero { padding: 2.5rem 1.25rem 2.25rem; }
}
</style>
